import axiosInstance from "src/common/axiosInstance";
import EmptyState from "../EmptyState";
import { CallTranscripts } from "../../Documents/RecordingTranscript";
import ActivityReminderPanel from "./ActivityReminderPanel";
import ActivitySchedulePanel from "./ActivitySchedulePanel";
import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Mail,
  MessageCircle,
  Pen,
  Phone,
  Trash2,
  Users,
} from "lucide-react";

const activityMeta = {
  CALL: {
    icon: Phone,
    color: "bg-blue-50 text-blue-600 border border-blue-100",
    label: "Calls",
  },
  EMAIL: {
    icon: Mail,
    color: "bg-cyan-50 text-cyan-600 border border-cyan-100",
    label: "Emails",
  },
  WHATSAPP: {
    icon: MessageCircle,
    color: "bg-emerald-50 text-emerald-600 border border-emerald-100",
    label: "WhatsApp",
  },
  MEETING: {
    icon: Users,
    color: "bg-cyan-50 text-cyan-600 border border-cyan-100",
    label: "Meetings",
  },
  NOTE: {
    icon: FileText,
    color: "bg-gray-50 text-gray-600 border border-gray-100",
    label: "Notes",
  },
  FOLLOWUP: {
    icon: Calendar,
    color: "bg-amber-50 text-amber-600 border border-amber-100",
    label: "Follow-ups",
  },
};

const typeOrder = ["CALL", "FOLLOWUP", "WHATSAPP", "EMAIL", "MEETING", "NOTE"];

const statusClasses = {
  PENDING: "bg-amber-50 text-amber-700 border border-amber-200/60",
  COMPLETED: "bg-emerald-50 text-emerald-700 border border-emerald-200/60",
  CANCELLED: "bg-rose-50 text-rose-700 border border-rose-200/60",
};

function SectionHeader({ title, description }) {
  return (
    <div className="border-b border-gray-100 pb-0">

      <h2 className="text-lg font-semibold tracking-tight text-slate-900">
        {title}
      </h2>

      <p className="mt-1 text-sm text-slate-500">{description}</p>
    </div>
  );
}

function ActivityFilterBar({ filters, activeFilter, onSelectFilter }) {
  return (
    <div className="flex flex-wrap gap-2">

      {filters.map((filter) => {
        const Icon =
          filter.type === "ALL" ? Activity : activityMeta[filter.type].icon;
        const isActive = activeFilter === filter.type;

        return (
          <button
            key={filter.type}
            onClick={() => onSelectFilter(filter.type)}
            className={`flex items-center gap-2 rounded-2xl border px-4 py-2 transition-all duration-200 font-medium text-sm 
              ${isActive
                ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm"
                : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`}
          >
            <Icon size={16} />
            <span>{filter.label}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide ${isActive
                  ? "bg-blue-200/60 text-blue-800"
                  : "bg-gray-100 text-gray-600"
                }`}
            >
              {filter.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ActivityCard({ activity, onEdit, markCompleted, manageActivity, showReminders, showTranscripts, deleteActivity }) {
  const meta = activityMeta[activity.type] || activityMeta.NOTE;
  const Icon = meta.icon;

  return (
    <div className="rounded-2xl border border-gray-150 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-100/70">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-6">
        <div className="flex flex-1 gap-4">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-sm ${meta.color}`}
          >
            <Icon size={20} />
          </div>

          <div className="flex-1 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">

              <h3 className="font-semibold tracking-tight text-gray-900">
                {meta.label}
              </h3>

              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium tracking-wide uppercase ${statusClasses[activity.status]
                  }`}
              >
                {activity.status}
              </span>

              {activity.response_type && (
                <span className="rounded-full bg-gray-50 border border-gray-250/50 px-2.5 py-0.5 text-xs text-gray-600 font-medium">
                  {activity.response_type}
                </span>
              )}

            </div>

            {activity.note && (
              <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600 font-normal">
                {activity.note}
              </p>
            )}

            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1 text-xs font-medium text-gray-400">

              {activity.scheduled_at && (
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Clock size={14} className="text-gray-400" />

                  <span>
                    Scheduled:{" "}
                    <span className="text-gray-700">
                      {new Date(activity.scheduled_at).toLocaleString()}
                    </span>
                  </span>

                </div>
              )}

              {activity.completed_at && (
                <div className="flex items-center gap-1.5 text-emerald-600">
                  <CheckCircle2 size={14} />

                  <span>
                    Completed:{" "}

                    <span className="font-semibold">
                      {new Date(activity.completed_at).toLocaleString()}
                    </span>

                  </span>

                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-50 pt-4 sm:flex-col sm:items-end sm:justify-between sm:border-t-0 sm:pt-0">
          
          <div className="sm:text-right">
            
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Created
            </p>
            
            <p className="mt-0.5 text-xs font-medium text-gray-500">
              {new Date(activity.created_at).toLocaleString()}
            </p>

          </div>

          <div className="flex gap-2">

            <button
              onClick={() => onEdit?.(activity)}
              className="rounded-xl border border-gray-200 p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            >
              <Pen size={15} />
            </button>

            {activity.type === "CALL" && <button type="button" onClick={() => showTranscripts(activity.id)} className="rounded-xl border px-3 py-2 text-xs">Transcripts & summaries</button>}
            <button onClick={() => showReminders(activity.id)} className="rounded-xl border px-3 py-2 text-xs">Reminders</button>
            {activity.status === "PENDING" && <button onClick={() => manageActivity(activity.id)} className="rounded-xl border px-3 py-2 text-xs">Manage schedule</button>}
            {activity.status === "PENDING" && (
              <button
                onClick={() => markCompleted(activity.id)}
                className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-150"
              >
                Complete
              </button>
            )}

            <button
              onClick={() => deleteActivity(activity.id)}
              className="rounded-xl border border-gray-200 p-2 text-gray-400 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeadTabs({ leadId, workspaceId, onEdit ,activitiesRefreshKey}) {
  const [transcriptCall, setTranscriptCall] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managedActivity, setManagedActivity] = useState(null);
  const [reminderActivity, setReminderActivity] = useState(null);
  useEffect(() => { setManagedActivity(null); setReminderActivity(null); }, [leadId]);
  const [activeFilter, setActiveFilter] = useState("ALL");

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`leads/${leadId}/timeline`);
      setActivities(
        Array.isArray(res.data) ? res.data : res.data.data || []
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities, activitiesRefreshKey]);

  function markCompleted(id) {
    setReminderActivity(null);
    setManagedActivity({ id, operation: "complete" });
  }

  async function deleteActivity(id) {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await axiosInstance.delete(`/lead-activities/${id}`);
      fetchActivities();
    } catch (err) {
      console.error(err);
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-sm font-medium text-gray-400 animate-pulse">
        Loading activities...
      </div>
    );
  }

  if (!activities.length) {
    return (
      <div className="space-y-5 pb-8">
        <SectionHeader
          title="Activity history"
          description="Calls, messages, meetings, notes, and follow-ups."
        />
        <EmptyState message="No activities yet" />
      </div>
    );
  }

  const availableTypes = typeOrder.filter((type) =>
    activities.some((a) => a.type === type)
  );

  const filters = [
    { type: "ALL", label: "All", count: activities.length },
    ...availableTypes.map((type) => ({
      type,
      label: activityMeta[type].label,
      count: activities.filter((a) => a.type === type).length,
    })),
  ];

  const filteredActivities =
    activeFilter === "ALL"
      ? activities
      : activities.filter((a) => a.type === activeFilter);

  return (
    <div className="space-y-5 pb-8">
      <SectionHeader
        title="Activity history"
        description="Calls, messages, meetings, notes, and follow-ups."
      />

      {transcriptCall && <CallTranscripts key={`${workspaceId}:${transcriptCall}`} workspaceId={workspaceId || localStorage.getItem("autoappy_current_workspace_id")} callId={transcriptCall} onClose={() => setTranscriptCall(null)} />}
      {reminderActivity && <ActivityReminderPanel key={`${leadId}:${reminderActivity}`} activityId={reminderActivity} onClose={() => setReminderActivity(null)} />}
      {managedActivity && <ActivitySchedulePanel
        key={`${leadId}:${managedActivity.id}:${managedActivity.operation}`}
        activityId={managedActivity.id}
        initialOperation={managedActivity.operation}
        onClose={() => setManagedActivity(null)}
        onSaved={() => { setManagedActivity(null); fetchActivities(); }}
      />}
      <ActivityFilterBar
        filters={filters}
        activeFilter={activeFilter}
        onSelectFilter={setActiveFilter}
      />

      {!filteredActivities.length ? (
        <EmptyState message="No activities yet" />
      ) : (
        <div className="space-y-4">
          {filteredActivities.map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              onEdit={onEdit}
              markCompleted={markCompleted}
              showTranscripts={setTranscriptCall}
              showReminders={id => { setManagedActivity(null); setReminderActivity(id); }}
              manageActivity={id => { setReminderActivity(null); setManagedActivity({ id, operation: "reschedule" }); }}
              deleteActivity={deleteActivity}
            />
          ))}
        </div>
      )}
    </div>
  );
}
